// src/workspace/ui/slots/MemoryCookieSlot.jsx
// ─────────────────────────────────────────────────────────────────────────────
// RIGHT PANEL: ultra-compact single bar — mini egg · count · toggle · OPEN btn
// MODAL:       full-screen — big egg + block feed + type legend + education
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

const MONO = 'JetBrains Mono,monospace';
const C = {
  void: '#020508', deep: '#060b14', surface: '#0a1120',
  b0: '#0f1a2e', b1: '#1e293b', b2: '#334155',
  t1: '#e2e8f0', t2: '#b6c3d8', t3: '#8396b3', t4: '#627795',
  cyan: '#22d3ee', violet: '#a78bfa', green: '#4ade80',
  gold: '#fbbf24', red: '#f87171', pink: '#f472b6',
};

const BLOCK_TYPES = {
  dataset: { label: 'Dataset', color: '#22d3ee', icon: '◈' },
  // agent:   { label: 'Agent',   color: '#a78bfa', icon: '⬡' },
  finding: { label: 'Finding', color: '#4ade80', icon: '◉' },
  model:   { label: 'Model',   color: '#fbbf24', icon: '⚡' },
  paper:   { label: 'Paper',   color: '#f472b6', icon: '⬢' },
};

const EGG_PATH = 'M 60 6 C 95 6,114 40,114 76 C 114 118,90 142,60 142 C 30 142,6 118,6 76 C 6 40,25 6,60 6 Z';
const EGG = { cx: 60, cy: 74, rx: 54, ry: 68 };
const BW = 11, BH = 5, GX = 2, GY = 2, CW = BW + GX, CH = BH + GY;

function insideEgg(bx, by) {
  const px = bx + BW / 2, py = by + BH / 2;
  return (Math.pow((px - EGG.cx) / (EGG.rx * 0.87), 2) + Math.pow((py - EGG.cy) / (EGG.ry * 0.87), 2)) <= 1;
}

const VALID_CELLS = (() => {
  const cells = [], xS = EGG.cx - EGG.rx + 4, yB = EGG.cy + EGG.ry - 6;
  const cols = Math.floor((EGG.rx * 2 - 8) / CW), rows = Math.floor((EGG.ry * 2 - 12) / CH);
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) {
      const bx = xS + c * CW, by = yB - (r + 1) * CH;
      if (insideEgg(bx, by)) cells.push({ x: bx, y: by });
    }
  return cells;
})();
const MAX_BLOCKS = VALID_CELLS.length;

const SEED_EVENTS = [
  { type: 'dataset', text: 'iris.csv loaded',             detail: '150 rows x 5 cols. Columns: sepal_length, sepal_width, petal_length, petal_width, species. Class balance: equal 3-way split.' },
  { type: 'agent',   text: 'Auto EDA completed',          detail: 'Full exploratory analysis run by Data Scientist agent. Generated distribution plots, correlation heatmap, and outlier detection report.' },
  { type: 'finding', text: '3 species split by PCA',      detail: 'PCA 2-component projection shows clean separation of Setosa, Versicolor, Virginica with <2% overlap zone.' },
  { type: 'model',   text: 'KNN: 96.7% accuracy',         detail: 'K=5 KNN, 80/20 train-test split. Accuracy 96.7%, F1 macro 0.967. Outperforms Logistic Regression (94.1%).' },
  { type: 'dataset', text: 'housing_data.csv merged',     detail: '2,400 rows post-dedup from 3 source files. Key cols: sqft, bedrooms, price_k, city. 4 null rows dropped.' },
  { type: 'agent',   text: 'Feature engineering run',     detail: 'Agent created price_per_sqft, age_bucket, city_tier. Dropped 4 near-zero-variance columns. Saved feature manifest.' },
  { type: 'finding', text: 'sqft strongest predictor',    detail: 'Pearson r=0.83 with price. Next: city_tier (r=0.71), property age (r=-0.44). Multicollinearity checked.' },
  { type: 'model',   text: 'XGBoost beats LinearReg',     detail: 'XGBoost RMSE 18.2K vs LinearReg 31.4K. Top features: sqft (34%), city_tier (28%), age (18%).' },
  { type: 'paper',   text: 'Attention Is All You Need',   detail: 'Transformer architecture (Vaswani et al). Key insights: multi-head self-attention, positional encoding, no recurrence.' },
  { type: 'agent',   text: 'Research summary written',    detail: '4-paragraph summary covering datasets explored, models compared, and top 3 conclusions. Exported to workspace.' },
  { type: 'finding', text: 'Outlier cluster: revenue col',detail: 'k-means cluster 4 (n=23) shows revenue 3.2x above mean. Likely enterprise tier — flagged for separate model.' },
  { type: 'dataset', text: 'churn_q4.csv — 5.2K rows',   detail: 'Telecom churn dataset. 5,200 rows, 14 cols. Target: churn (binary). Class imbalance: 18.4% positive rate.' },
  { type: 'model',   text: 'RF AUC 0.91 vs 0.72 base',   detail: 'Random Forest 200 trees, max_depth=8. AUC 0.91, Precision 0.84, Recall 0.79. Baseline majority class AUC 0.72.' },
  { type: 'finding', text: 'Tenure <6mo = high churn',    detail: 'Customers with tenure < 6 months churn 41% vs 7% for >24m. SHAP: tenure top feature across all tree splits.' },
  { type: 'agent',   text: 'Correlation matrix analyzed', detail: 'Agent found multicollinearity: monthly_charges + total_charges r=0.96. Dropped total_charges before final model fit.' },
];

const SIM_EVENTS = [
  { type: 'dataset', text: 'sales_2026.csv — 8K rows',   detail: 'Quarterly sales data. 8,000 rows, 22 columns. Date range Jan–Dec 2025. Region breakdown included.' },
  { type: 'finding', text: 'Q3 revenue anomaly found',   detail: 'Revenue dipped 22% in Q3 despite flat volume. Root cause traced to discount surge in Region 4.' },
  { type: 'agent',   text: '4 natural clusters found',   detail: 'UMAP + HDBSCAN identified 4 customer segments. Silhouette score 0.71. Segment profiles exported.' },
  { type: 'model',   text: 'LightGBM accuracy: 0.943',   detail: 'LightGBM, 500 rounds lr=0.05. Test accuracy 0.943. Training time 4.2s. Saved checkpoint.' },
  { type: 'paper',   text: 'SHAP explainability paper',  detail: 'Lundberg & Lee (2017). Applied SHAP to churn model. Top: tenure, support_calls, contract_type.' },
  { type: 'finding', text: 'Age & income neg correlated',detail: 'Partial correlation controlling for region: r=-0.31, p=0.0003. Younger high-earners churn more often.' },
  { type: 'dataset', text: '200 chest X-ray images',     detail: 'DICOM format, 224x224 preprocessed. Labels: normal (152), pneumonia (48). Train/val/test 70/15/15.' },
  { type: 'agent',   text: 'Hypothesis test p<0.05',     detail: 'Two-sample t-test. t=-4.21, p=0.0003. Null rejected. Significant difference confirmed between segments.' },
];

function ago(ts) {
  const d = Date.now() - ts;
  if (d < 60000) return `${Math.floor(d / 1000)}s ago`;
  if (d < 3600000) return `${Math.floor(d / 60000)}m ago`;
  return `${Math.floor(d / 3600000)}h ago`;
}

// ── SVG Egg (reused at two sizes) ────────────────────────────────────────────
function EggSvg({ blocks, newBlockId, memoryOn, width, height }) {
  const filled = Math.min(blocks.length, MAX_BLOCKS);
  const idSuffix = width < 50 ? 'sm' : 'lg';
  return (
    <svg viewBox="0 0 120 162" width={width} height={height} style={{ overflow: 'visible',
      filter: memoryOn && width > 50 ? `drop-shadow(0 0 20px ${C.cyan}44)` : 'none',
      transition: 'filter .4s',
      animation: memoryOn && width > 50 ? 'mc-breathe 3s ease-in-out infinite' : 'none',
    }}>
      <defs>
        <clipPath id={`mc-clip-${idSuffix}`}><path d={EGG_PATH} /></clipPath>
        <radialGradient id={`mc-bg-${idSuffix}`} cx="50%" cy="36%" r="65%">
          <stop offset="0%" stopColor="#0d1a2e" /><stop offset="100%" stopColor="#020608" />
        </radialGradient>
        <radialGradient id={`mc-shine-${idSuffix}`} cx="28%" cy="20%" r="50%">
          <stop offset="0%" stopColor="white" stopOpacity="0.16" />
          <stop offset="60%" stopColor="white" stopOpacity="0.04" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Glow ring */}
      <path d={EGG_PATH} fill="none" stroke={C.cyan} strokeWidth="4"
        strokeOpacity={memoryOn ? 0.07 : 0} style={{ transition: 'stroke-opacity .4s' }} />
      <path d={EGG_PATH} fill="none" stroke={C.cyan} strokeWidth="1.5"
        strokeOpacity={memoryOn ? 0.55 : 0.12}
        style={{ animation: memoryOn ? 'mc-ring 2.8s ease-in-out infinite' : 'none' }} />
      {/* Body */}
      <path d={EGG_PATH} fill={`url(#mc-bg-${idSuffix})`} />
      {/* Grid lines (only big) */}
      {width > 50 && (
        <g clipPath={`url(#mc-clip-${idSuffix})`} opacity="0.04">
          {Array.from({ length: 14 }).map((_, i) => (
            <line key={`h${i}`} x1="0" y1={6 + i * 10} x2="120" y2={6 + i * 10} stroke={C.cyan} strokeWidth="0.5" />
          ))}
          {Array.from({ length: 9 }).map((_, i) => (
            <line key={`v${i}`} x1={6 + i * 13} y1="0" x2={6 + i * 13} y2="148" stroke={C.cyan} strokeWidth="0.5" />
          ))}
        </g>
      )}
      {/* Blocks */}
      <g clipPath={`url(#mc-clip-${idSuffix})`}>
        {VALID_CELLS.slice(0, filled).map((cell, idx) => {
          const b = blocks[idx]; if (!b) return null;
          const col = BLOCK_TYPES[b.type]?.color ?? C.t4;
          const isNew = b.id === newBlockId;
          return (
            <rect key={idx} x={cell.x} y={cell.y} width={BW} height={BH}
              fill={col} fillOpacity={0.6 + (b.jitter || 0.5) * 0.36} rx="1"
              style={isNew ? { animation: 'mc-flash .9s ease-out, mc-drop .35s ease-out' } : undefined}
            />
          );
        })}
      </g>
      {/* Glass + edge */}
      <path d={EGG_PATH} fill={`url(#mc-shine-${idSuffix})`} />
      <path d={EGG_PATH} fill="none"
        stroke={memoryOn ? C.cyan : C.b2} strokeWidth="0.8"
        strokeOpacity={memoryOn ? 0.5 : 0.2} style={{ transition: 'stroke .35s' }} />
      {/* Key label (only big) */}
      {width > 50 && (
        <text x="60" y="157" textAnchor="middle" fontSize="6.5" fill={C.cyan}
          fillOpacity={memoryOn ? 0.6 : 0.2} fontFamily={MONO} fontWeight="800" letterSpacing="0.1em"
          style={{ transition: 'fill-opacity .95s' }}>
          YOUR COOKIE
        </text>
      )}
    </svg>
  );
}

// ── Block detail popup (inside modal) ────────────────────────────────────────
function BlockPopup({ block, total, onClose }) {
  if (!block) return null;
  const bt  = BLOCK_TYPES[block.type] ?? { color: C.t4, icon: '◈', label: 'Event' };
  const idx = total - 1; // last created is most recent
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 10003,
      background: 'rgba(2,5,8,.7)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: C.deep, border: `1px solid ${bt.color}55`,
        borderTop: `3px solid ${bt.color}`, borderRadius: 6,
        padding: '22px 24px', maxWidth: 420, width: '90%',
        boxShadow: `0 0 50px ${bt.color}22`,
        animation: 'mc-fadein .18s ease-out',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <span style={{ fontSize: 13 }}>{bt.icon}</span>
          <span style={{ fontSize: 8, fontFamily: MONO, fontWeight: 800, color: bt.color, letterSpacing: '.14em' }}>
            {bt.label.toUpperCase()} BLOCK
          </span>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 8, fontFamily: MONO, color: C.t4 }}>{ago(block.ts)}</span>
        </div>
        <div style={{ fontSize: 13, color: C.t1, fontWeight: 700, lineHeight: 1.45, marginBottom: 10 }}>
          {block.text}
        </div>
        <div style={{
          fontSize: 11, color: C.t3, lineHeight: 1.65,
          padding: '10px 12px', background: C.void,
          border: `1px solid ${C.b1}`, borderRadius: 3, marginBottom: 14,
        }}>
          {block.detail}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{
            padding: '4px 8px', background: `${bt.color}10`,
            border: `1px solid ${bt.color}30`, borderRadius: 3,
            fontSize: 8, fontFamily: MONO, color: bt.color,
          }}>🔒 ENCRYPTED</div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{
            padding: '7px 16px', background: `${bt.color}15`,
            border: `1px solid ${bt.color}44`, color: bt.color,
            fontSize: 9, fontFamily: MONO, fontWeight: 800, cursor: 'pointer', borderRadius: 3,
          }}>CLOSE ✕</button>
        </div>
      </div>
    </div>
  );
}

// ── Full-screen modal ─────────────────────────────────────────────────────────
function MemoryModal({ blocks, newBlockId, memoryOn, onToggle, onClose }) {
  const [selected, setSelected] = useState(null);
  const [tab, setTab]           = useState('feed');
  const typeCounts = useMemo(() => {
    const c = {}; blocks.forEach(b => { c[b.type] = (c[b.type] || 0) + 1; }); return c;
  }, [blocks]);
  const fillPct = Math.min(100, Math.round((Math.min(blocks.length, MAX_BLOCKS) / MAX_BLOCKS) * 100));
  const reversed = useMemo(() => [...blocks].reverse(), [blocks]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10001,
      background: 'rgba(2,5,8,.85)', backdropFilter: 'blur(14px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 16px',
    }}>
      <style>{`
        @keyframes mc-ring    { 0%,100%{stroke-opacity:.3}  50%{stroke-opacity:.9}  }
        @keyframes mc-flash   { 0%{filter:brightness(3.5)} 100%{filter:brightness(1)} }
        @keyframes mc-drop    { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes mc-breathe { 0%,100%{filter:drop-shadow(0 0 6px #22d3ee44)} 50%{filter:drop-shadow(0 0 22px #22d3eeaa)} }
        @keyframes mc-fadein  { from{opacity:0;transform:scale(.96)} to{opacity:1;transform:scale(1)} }
        @keyframes mc-pulse   { 0%,100%{opacity:.45} 50%{opacity:1} }
      `}</style>

      <div style={{
        background: C.deep, border: `1px solid ${C.b1}`,
        borderTop: `3px solid ${C.cyan}`, borderRadius: 8,
        width: '100%', maxWidth: 860, maxHeight: '88vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: `0 0 80px ${C.cyan}18`, animation: 'mc-fadein .22s ease-out',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '13px 20px', borderBottom: `1px solid ${C.b0}`, flexShrink: 0,
        }}>
          <span style={{ fontSize: 8, fontFamily: MONO, fontWeight: 800, color: C.cyan, letterSpacing: '.18em' }}>
            RESEARCH MEMORY COOKIE
          </span>
          {memoryOn && <div style={{ width: 5, height: 5, borderRadius: '50%', background: C.green, animation: 'mc-pulse 1.6s infinite' }} />}
          <div style={{ flex: 1 }} />
          {/* Privacy badge */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px',
            background: `${C.gold}0c`, border: `1px solid ${C.gold}25`, borderRadius: 3,
          }}>
            <span style={{ fontSize: 9 }}>🔒</span>
            <span style={{ fontSize: 7.5, fontFamily: MONO, color: C.gold, letterSpacing: '.06em' }}>
              ENCRYPTED · ONLY YOUR KEY CAN OPEN THIS
            </span>
          </div>
          {/* Toggle */}
          <button onClick={onToggle} style={{
            display: 'flex', alignItems: 'center', gap: 5, padding: '4px 9px 4px 5px',
            background: memoryOn ? `${C.cyan}12` : C.void,
            border: `1px solid ${memoryOn ? C.cyan + '44' : C.b2}`,
            borderRadius: 20, cursor: 'pointer', transition: 'all .25s',
          }}>
            <div style={{
              position: 'relative', width: 26, height: 13,
              background: memoryOn ? `${C.cyan}30` : C.b1,
              borderRadius: 7, border: `1px solid ${memoryOn ? C.cyan + '55' : C.b2}`,
            }}>
              <div style={{
                position: 'absolute', top: 1.5, left: memoryOn ? 13 : 1.5,
                width: 10, height: 10, borderRadius: '50%',
                background: memoryOn ? C.cyan : C.t4,
                boxShadow: memoryOn ? `0 0 8px ${C.cyan}bb` : 'none',
                transition: 'left .22s, background .22s',
              }} />
            </div>
            <span style={{ fontSize: 8, fontFamily: MONO, fontWeight: 700, color: memoryOn ? C.cyan : C.t4 }}>
              {memoryOn ? 'COLLECTING' : 'PAUSED'}
            </span>
          </button>
          <button onClick={onClose} style={{
            padding: '5px 10px', background: C.void, border: `1px solid ${C.b1}`,
            color: C.t3, fontSize: 9, fontFamily: MONO, cursor: 'pointer', borderRadius: 3,
          }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* Left column — egg */}
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '20px 20px',
            borderRight: `1px solid ${C.b0}`, minWidth: 220, flexShrink: 0,
          }}>
            <EggSvg blocks={blocks} newBlockId={newBlockId} memoryOn={memoryOn} width={180} height={240} />

            {/* Stats */}
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              {[
                { l: 'BLOCKS', v: blocks.length, c: C.cyan },
                { l: 'FILL',   v: `${fillPct}%`, c: C.violet },
                { l: 'CAP',    v: MAX_BLOCKS,    c: C.t4 },
              ].map(s => (
                <div key={s.l} style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  padding: '4px 9px', background: s.c + '10',
                  border: `1px solid ${s.c}28`, borderRadius: 3,
                }}>
                  <span style={{ fontSize: 11, fontFamily: MONO, fontWeight: 800, color: s.c }}>{s.v}</span>
                  <span style={{ fontSize: 6.5, fontFamily: MONO, color: s.c + '99', letterSpacing: '.07em', marginTop: 1 }}>{s.l}</span>
                </div>
              ))}
            </div>

            {/* Type chips */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 10, justifyContent: 'center' }}>
              {Object.entries(BLOCK_TYPES).map(([k, v]) => {
                const cnt = typeCounts[k] || 0; if (!cnt) return null;
                return (
                  <div key={k} style={{
                    display: 'flex', alignItems: 'center', gap: 3,
                    padding: '2px 7px', background: v.color + '12',
                    border: `1px solid ${v.color}30`, borderRadius: 10,
                  }}>
                    <div style={{ width: 5, height: 5, borderRadius: 1, background: v.color }} />
                    <span style={{ fontSize: 7, fontFamily: MONO, color: v.color }}>{cnt} {v.label.toUpperCase()}</span>
                  </div>
                );
              })}
            </div>

            {/* Chain status */}
            <div style={{
              marginTop: 12, display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 10px', background: `${C.green}0c`,
              border: `1px solid ${C.green}28`, borderRadius: 3,
            }}>
              <span style={{ fontSize: 9 }}>⛓</span>
              <span style={{ fontSize: 8, fontFamily: MONO, color: C.green, letterSpacing: '.06em' }}>ON-CHAIN · PERMANENT</span>
            </div>
          </div>

          {/* Right column — tabs */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', borderBottom: `1px solid ${C.b0}`, padding: '0 20px', flexShrink: 0 }}>
              {[
                { id: 'feed',  label: '📋 MEMORY BLOCKS' },
                { id: 'about', label: '❓ HOW IT WORKS' },
              ].map(t => (
                <button key={t.id} onClick={() => setTab(t.id)} style={{
                  padding: '11px 14px', border: 'none',
                  borderBottom: tab === t.id ? `2px solid ${C.cyan}` : '2px solid transparent',
                  background: 'transparent', color: tab === t.id ? C.cyan : C.t4,
                  fontSize: 8, fontFamily: MONO, fontWeight: 700, cursor: 'pointer',
                  letterSpacing: '.1em', transition: 'color .12s',
                }}>{t.label}</button>
              ))}
            </div>

            {/* Feed */}
            {tab === 'feed' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
                <div style={{ fontSize: 8.5, color: C.t4, fontFamily: MONO, marginBottom: 10 }}>
                  {blocks.length} total blocks · click any row to see full context
                </div>
                {reversed.map((ev) => {
                  const bt = BLOCK_TYPES[ev.type] ?? { color: C.t4, icon: '◈', label: '' };
                  return (
                    <div key={ev.id} onClick={() => setSelected(ev)} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 10px', marginBottom: 4,
                      background: C.void, border: `1px solid ${C.b1}`,
                      borderLeft: `3px solid ${bt.color}`,
                      borderRadius: 3, cursor: 'pointer', transition: 'background .1s',
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = C.surface}
                      onMouseLeave={e => e.currentTarget.style.background = C.void}
                    >
                      <span style={{ fontSize: 11, flexShrink: 0 }}>{bt.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 10.5, color: C.t1, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ev.text}
                        </div>
                        <div style={{ fontSize: 8, color: C.t4, fontFamily: MONO, marginTop: 2 }}>
                          {bt.label.toUpperCase()} · {ago(ev.ts)}
                        </div>
                      </div>
                      <span style={{ fontSize: 8, color: C.t4, flexShrink: 0 }}>VIEW →</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* About */}
            {tab === 'about' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                {[
                  { c: C.cyan,   i: '⬡', t: 'Every action becomes a block',
                    b: 'Loading datasets, running agents, recording findings — each event produces a coloured block. Blocks fill the egg from the bottom. Your research history, in glass.' },
                  { c: C.gold,   i: '🔑', t: 'Your key. Your memory.',
                    b: 'All blocks are encrypted client-side with a private key derived from your personal seed phrase. The server never sees the plaintext. Not even this platform can read your memory. Mathematically impossible without your key.' },
                  { c: C.violet, i: '⛓',  t: 'Permanently yours on-chain',
                    b: 'Encrypted memory is committed to Arweave (permanent storage) with a pointer on-chain. It stays forever, belongs to you, and you can export or migrate it to any compatible platform.' },
                  { c: C.green,  i: '🤖', t: 'Agents learn from your history',
                    b: 'When a session starts, your AI agents read the cookie context — datasets explored, models run, findings recorded. You get smarter results without re-explaining your work every time.' },
                  { c: C.pink,   i: '🧹', t: 'Toggle off or delete anytime',
                    b: 'Flip the switch to pause collection. Delete individual blocks or the entire cookie instantly. Nothing persists on any server — only what you chose to write on-chain.' },
                ].map((item, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: 14, marginBottom: 16,
                    padding: '13px 15px', background: item.c + '07',
                    border: `1px solid ${item.c}20`, borderRadius: 5,
                  }}>
                    <span style={{ fontSize: 17, flexShrink: 0, marginTop: 1 }}>{item.i}</span>
                    <div>
                      <div style={{ fontSize: 10, fontFamily: MONO, fontWeight: 800, color: item.c, letterSpacing: '.06em', marginBottom: 6 }}>
                        {item.t.toUpperCase()}
                      </div>
                      <div style={{ fontSize: 11, color: C.t3, lineHeight: 1.65 }}>{item.b}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {selected && <BlockPopup block={selected} total={blocks.length} onClose={() => setSelected(null)} />}
    </div>
  );
}

// ── MAIN EXPORT — tight right-panel bar ──────────────────────────────────────
export default function MemoryCookieSlot() {
  const simIdx = useRef(0);
  const [blocks, setBlocks] = useState(() =>
    SEED_EVENTS.map((e, i) => ({
      ...e, id: `s${i}`,
      ts: Date.now() - (SEED_EVENTS.length - i) * 720000,
      jitter: (i * 0.137 + 0.3) % 1.0,
    }))
  );
  const [memoryOn, setMemoryOn]     = useState(true);
  const [newBlockId, setNewBlockId] = useState(null);
  const [modalOpen, setModalOpen]   = useState(false);

  useEffect(() => {
    if (!memoryOn) return;
    const id = setInterval(() => {
      const ev = SIM_EVENTS[simIdx.current % SIM_EVENTS.length];
      simIdx.current += 1;
      const block = { ...ev, id: `sim_${Date.now()}`, ts: Date.now(), jitter: Math.random() };
      setBlocks(prev => { const n = [...prev, block]; return n.length > MAX_BLOCKS ? n.slice(-MAX_BLOCKS) : n; });
      setNewBlockId(block.id);
      setTimeout(() => setNewBlockId(null), 1400);
    }, 10000);
    return () => clearInterval(id);
  }, [memoryOn]);

  const handleToggle = useCallback(() => setMemoryOn(p => !p), []);
  const fillPct = Math.min(100, Math.round((Math.min(blocks.length, MAX_BLOCKS) / MAX_BLOCKS) * 100));
  const accent  = memoryOn ? C.cyan : C.t4;

  const typeCounts = useMemo(() => {
    const c = {}; blocks.forEach(b => { c[b.type] = (c[b.type] || 0) + 1; }); return c;
  }, [blocks]);

  return (
    <>
      <style>{`
        @keyframes mc-pulse   { 0%,100%{opacity:.45} 50%{opacity:1} }
        @keyframes mc-ring    { 0%,100%{stroke-opacity:.3} 50%{stroke-opacity:.9} }
        @keyframes mc-flash   { 0%{filter:brightness(3.5)} 100%{filter:brightness(1)} }
        @keyframes mc-drop    { from{opacity:0;transform:translateY(-7px)} to{opacity:1;transform:translateY(0)} }
        @keyframes mc-breathe { 0%,100%{filter:drop-shadow(0 0 5px #22d3ee33)} 50%{filter:drop-shadow(0 0 18px #22d3ee99)} }
        @keyframes mc-fadein  { from{opacity:0;transform:scale(.96)} to{opacity:1;transform:scale(1)} }
      `}</style>

      {/* ── Content (flat — lives inside unified shell tab) ── */}
      <div>

        {/* ── Header row ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px 6px' }}>
          <span style={{ fontSize: 7.5, fontFamily: MONO, fontWeight: 800, color: accent, letterSpacing: '.14em' }}>
            RESEARCH MEMORY
          </span>
          {memoryOn && <div style={{ width: 5, height: 5, borderRadius: '50%', background: C.green, animation: 'mc-pulse 1.6s infinite' }} />}
          <div style={{ flex: 1 }} />
          {/* Toggle */}
          <div onClick={handleToggle} title={memoryOn ? 'Pause' : 'Resume'} style={{
            position: 'relative', width: 26, height: 13, flexShrink: 0,
            background: memoryOn ? `${C.cyan}30` : C.b1,
            border: `1px solid ${memoryOn ? C.cyan + '55' : C.b2}`,
            borderRadius: 7, cursor: 'pointer', transition: 'all .22s',
          }}>
            <div style={{
              position: 'absolute', top: 1, left: memoryOn ? 13 : 1,
              width: 11, height: 11, borderRadius: '50%',
              background: memoryOn ? C.cyan : C.t4,
              boxShadow: memoryOn ? `0 0 6px ${C.cyan}bb` : 'none',
              transition: 'left .22s, background .22s',
            }} />
          </div>
          <span style={{ fontSize: 7.5, fontFamily: MONO, fontWeight: 700, color: accent }}>{memoryOn ? 'ON' : 'OFF'}</span>
        </div>

        {/* ── Egg visual ── */}
        <div style={{
          display: 'flex', justifyContent: 'center', padding: '2px 0 4px',
          opacity: memoryOn ? 1 : 0.38, transition: 'opacity .35s',
        }}>
          <EggSvg blocks={blocks} newBlockId={newBlockId} memoryOn={memoryOn} width={190} height={290} />
        </div>

        {/* ── Stats row ── */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 6,
          padding: '0 10px 8px',
          opacity: memoryOn ? 1 : 0.42, transition: 'opacity .35s',
        }}>
          {[
            { l: 'BLOCKS', v: blocks.length, c: C.cyan },
            { l: 'FILL',   v: `${fillPct}%`, c: C.violet },
            { l: 'CHAIN',  v: '⛓ ON',        c: C.green },
          ].map(s => (
            <div key={s.l} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              padding: '4px 9px', background: s.c + '0e',
              border: `1px solid ${s.c}28`, borderRadius: 3, flex: 1,
            }}>
              <span style={{ fontSize: 10.5, fontFamily: MONO, fontWeight: 800, color: s.c }}>{s.v}</span>
              <span style={{ fontSize: 6.5, fontFamily: MONO, color: s.c + '99', letterSpacing: '.07em', marginTop: 1 }}>{s.l}</span>
            </div>
          ))}
        </div>

        {/* ── Block type chips ── */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 4, padding: '0 10px 8px',
          opacity: memoryOn ? 1 : 0.38, transition: 'opacity .35s',
        }}>
          {Object.entries(BLOCK_TYPES).map(([k, v]) => {
            const cnt = typeCounts[k] || 0; if (!cnt) return null;
            return (
              <div key={k} style={{
                display: 'flex', alignItems: 'center', gap: 3,
                padding: '2px 6px', background: v.color + '10',
                border: `1px solid ${v.color}2e`, borderRadius: 10,
              }}>
                <div style={{ width: 5, height: 5, borderRadius: 1, background: v.color }} />
                <span style={{ fontSize: 7, fontFamily: MONO, color: v.color }}>{cnt} {v.label.toUpperCase()}</span>
              </div>
            );
          })}
        </div>

        {/* ── Privacy note ── */}
        <div style={{
          margin: '0 10px 8px',
          padding: '6px 8px',
          background: `${C.gold}08`, border: `1px solid ${C.gold}20`,
          borderLeft: `2px solid ${C.gold}55`, borderRadius: 3,
          display: 'flex', alignItems: 'flex-start', gap: 6,
        }}>
          <span style={{ fontSize: 9, flexShrink: 0 }}>🔒</span>
          <span style={{ fontSize: 8, color: C.t4, lineHeight: 1.5, fontFamily: MONO }}>
            Encrypted with <span style={{ color: C.gold + 'cc' }}>your key only</span> —
            no server, no platform can ever read this.
          </span>
        </div>

        {/* ── Open full view button ── */}
        <div style={{ padding: '0 10px 10px' }}>
          <button onClick={() => setModalOpen(true)} style={{
            width: '100%', padding: '7px',
            background: `${C.cyan}12`, border: `1px solid ${C.cyan}33`,
            color: C.cyan, fontSize: 8, fontFamily: MONO, fontWeight: 800,
            cursor: 'pointer', borderRadius: 3, letterSpacing: '.1em',
            transition: 'background .15s',
          }}
            onMouseEnter={e => e.currentTarget.style.background = `${C.cyan}22`}
            onMouseLeave={e => e.currentTarget.style.background = `${C.cyan}12`}
        >VIEW MEMORY →
        </button>
        </div>

      </div>

      {/* ── Full-screen modal ──────────────────────────────────────── */}
      {modalOpen && (
        <MemoryModal
          blocks={blocks} newBlockId={newBlockId}
          memoryOn={memoryOn} onToggle={handleToggle}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}
